import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { usePaginateQuery } from "@/hooks";
import { api, handleApiError } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import {
  type Period,
  type TopValue,
  type TransactionTransferSummary,
  type UserScore,
  type UserTransactionTransferSummary,
} from "../types";

export type PeriodList = Paginate<Period<true, true>>;

export const periods = createQueryKeys("recognition-periods", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number | "current") => ({
    queryKey: [id],
    contextQueries: {
      point_bags: null,
      top_receivers: null,
      top_senders: null,
      received_summary: null,
      sent_summary: null,
      transactions_summary: null,
      top_values: null,
    },
  }),
});

const include = "values,pointBags";

export const fetchPeriodsQueryOptions = (
  page: number | undefined,
  search?: SearchRequest,
) =>
  queryOptions({
    ...periods.all(page, search),
    queryFn: () =>
      api
        .get<PeriodList>(`/recognition/periods`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPeriods = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPeriodsQueryOptions(page, search));

export const fetchPeriodQueryOptions = (id?: number | "current") =>
  queryOptions({
    ...periods.one(id),
    queryFn: async () => {
      try {
        const response = await api.get<Period<true, true>>(
          `/recognition/periods/${id}`,
          {
            params: { include },
          },
        );
        return response.data;
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return null;
        }

        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status !== 404
        ) {
          handleApiError(error, false);
        }

        throw error;
      }
    },
    enabled: id !== undefined,
    meta: { hideError: true },
  });

export const useFetchPeriod = (id?: number | "current") =>
  useQuery(fetchPeriodQueryOptions(id));

export const fetchPeriodTopReceiversQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.top_receivers,
    queryFn: () =>
      api
        .get<UserScore<true>[]>(`/recognition/periods/${id}/receivers/top`, {
          params: { include: "user.position" },
        })
        .then((response) => response.data),
  });

export const useFetchPeriodTopReceivers = (id?: number) =>
  useQuery(fetchPeriodTopReceiversQueryOptions(id));

export const fetchPeriodTopSendersQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.top_senders,
    queryFn: () =>
      api
        .get<UserScore<true>[]>(`/recognition/periods/${id}/senders/top`, {
          params: { include: "user.position" },
        })
        .then((response) => response.data),
  });

export const useFetchPeriodTopSenders = (id?: number) =>
  useQuery(fetchPeriodTopSendersQueryOptions(id));

export const fetchPeriodReceivedSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.received_summary,
    queryFn: () =>
      api
        .get<
          UserTransactionTransferSummary[]
        >(`/recognition/periods/${id}/receivers/summary`)
        .then((response) => response.data),
  });

export const useFetchPeriodReceivedSummary = (id?: number) =>
  useQuery(fetchPeriodReceivedSummaryQueryOptions(id));

export const fetchPeriodSentSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.sent_summary,
    queryFn: () =>
      api
        .get<
          UserTransactionTransferSummary[]
        >(`/recognition/periods/${id}/senders/summary`)
        .then((response) => response.data),
  });

export const useFetchPeriodSentSummary = (id?: number) =>
  useQuery(fetchPeriodSentSummaryQueryOptions(id));

export const fetchPeriodTopValuesQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.top_values,
    queryFn: () =>
      api
        .get<TopValue[]>(`/recognition/periods/${id}/values/top`)
        .then((response) => response.data),
  });

export const useFetchPeriodTopValues = (id?: number) =>
  useQuery(fetchPeriodTopValuesQueryOptions(id));

export const fetchPeriodTransactionSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.transactions_summary,
    queryFn: () =>
      api
        .get<
          TransactionTransferSummary[]
        >(`/recognition/periods/${id}/transactions/summary`)
        .then((response) => response.data),
  });

export const useFetchPeriodTransactionSummary = (id?: number) =>
  useQuery(fetchPeriodTransactionSummaryQueryOptions(id));
